// Phase JA.5 — useDailyAccaAnnouncement hook.
//
// Anon-readable single-row hook for the latest announcement (last 7d
// per RLS). Returns null until the 23:45 WAT job has written today's
// row. Never fabricates copy or counts.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface DailyAccaAnnouncementRow {
  day: string;
  headline: string;
  body: string;
  signals_published: number;
  signals_won: number;
  signals_lost: number;
  signals_void: number;
  win_rate: number | null;
  image_url: string | null;
  published_at: string;
}

function asInt(v: unknown): number {
  if (typeof v === "number") return Math.trunc(v);
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
  }
  return 0;
}

function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normaliseAnnouncement(
  row: Record<string, unknown> | null | undefined,
): DailyAccaAnnouncementRow | null {
  if (!row) return null;
  const day = row["day"];
  const headline = row["headline"];
  const body = row["body"];
  if (typeof day !== "string" || typeof headline !== "string" || typeof body !== "string") {
    return null;
  }
  return {
    day,
    headline,
    body,
    signals_published: asInt(row["signals_published"]),
    signals_won: asInt(row["signals_won"]),
    signals_lost: asInt(row["signals_lost"]),
    signals_void: asInt(row["signals_void"]),
    win_rate: asNumOrNull(row["win_rate"]),
    image_url: typeof row["image_url"] === "string" ? row["image_url"] : null,
    published_at: typeof row["published_at"] === "string" ? row["published_at"] : "",
  };
}

export interface UseDailyAccaAnnouncementState {
  announcement: DailyAccaAnnouncementRow | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDailyAccaAnnouncement(): UseDailyAccaAnnouncementState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [announcement, setAnnouncement] = useState<DailyAccaAnnouncementRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const { data, error: e } = await sb
        .from("daily_acca_announcement")
        .select(
          "day, headline, body, signals_published, signals_won, signals_lost, signals_void, win_rate, image_url, published_at",
        )
        .order("day", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (e) throw new Error(e.message);
      setAnnouncement(normaliseAnnouncement(data));
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load announcement");
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({ announcement, loading, error, refetch }),
    [announcement, loading, error, refetch],
  );
}
